"use client"

import Link from "next/link"
import { useSearchParams } from "next/navigation"
import { useState } from "react"

const TRADES = [
  ["hvac", "HVAC"],
  ["plumbing", "Plumbing"],
  ["electrical", "Electrical"],
  ["other", "Other"],
] as const
const LEVELS = [
  ["apprentice", "Apprentice"],
  ["journeyman", "Journeyman"],
  ["master", "Master"],
  ["lead", "Lead or foreman"],
  ["other", "Other"],
] as const

const ERRORS: Record<number, string> = {
  422: "Please check your phone number and ZIP.",
  429: "Too many tries. Please wait a minute and try again.",
}

const input =
  "w-full rounded-lg border border-white/15 bg-white/5 px-4 py-3 text-base text-white placeholder:text-white/30 focus:border-white/50 focus:outline-none"

function Chips({
  label,
  options,
  value,
  onChange,
}: {
  label: string
  options: readonly (readonly [string, string])[]
  value: string
  onChange: (v: string) => void
}) {
  return (
    <div role="group" aria-label={label}>
      <p className="mb-2 text-sm font-medium text-white/70">{label}</p>
      <div className="flex flex-wrap gap-2">
        {options.map(([v, text]) => (
          <button
            key={v}
            type="button"
            aria-pressed={value === v}
            onClick={() => onChange(v)}
            className={`min-h-12 rounded-full border px-5 text-base ${
              value === v
                ? "border-white bg-white text-[#0A0F1F]"
                : "border-white/20 text-white/80"
            }`}
          >
            {text}
          </button>
        ))}
      </div>
    </div>
  )
}

export function CareerCheckForm() {
  const source =
    useSearchParams().get("from") === "invite" ? "snapshot_invite" : "website"
  const [trade, setTrade] = useState("")
  const [level, setLevel] = useState("")
  const [name, setName] = useState("")
  const [phone, setPhone] = useState("")
  const [zip, setZip] = useState("")
  const [consent, setConsent] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState("")

  const ready =
    trade &&
    level &&
    name.trim() &&
    phone.trim() &&
    /^\d{5}$/.test(zip) &&
    consent

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!ready || busy) return
    setBusy(true)
    setError("")
    try {
      const res = await fetch("/api/v1/jobboard/career-check", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          phone: phone.trim(),
          home_zip: zip,
          trade,
          license_level: level,
          sms_consent: true,
          source,
        }),
      })
      if (!res.ok) {
        setError(
          ERRORS[res.status] ?? "Something went wrong. Please try again.",
        )
        setBusy(false)
        return
      }
      const { survey_url } = await res.json()
      if (!survey_url) throw new Error("no survey_url")
      window.location.href = survey_url
    } catch {
      setError("Something went wrong. Please try again.")
      setBusy(false)
    }
  }

  return (
    <form onSubmit={onSubmit} className="mt-8 space-y-6">
      <Chips
        label="Your trade"
        options={TRADES}
        value={trade}
        onChange={setTrade}
      />
      <Chips
        label="Your level"
        options={LEVELS}
        value={level}
        onChange={setLevel}
      />
      <div className="space-y-4">
        <label className="block text-sm font-medium text-white/70">
          Your name
          <input
            className={`${input} mt-2`}
            autoComplete="name"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </label>
        <label className="block text-sm font-medium text-white/70">
          Mobile number
          <input
            className={`${input} mt-2`}
            type="tel"
            autoComplete="tel"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
        </label>
        <label className="block text-sm font-medium text-white/70">
          Home ZIP
          <input
            className={`${input} mt-2`}
            inputMode="numeric"
            maxLength={5}
            pattern="[0-9]{5}"
            autoComplete="postal-code"
            value={zip}
            onChange={(e) => setZip(e.target.value.replace(/\D/g, ""))}
          />
        </label>
      </div>
      <label className="flex items-start gap-3 text-xs leading-snug text-white/60">
        <input
          type="checkbox"
          className="mt-0.5 h-5 w-5 shrink-0"
          checked={consent}
          onChange={(e) => setConsent(e.target.checked)}
        />
        <span>
          Text me my results and job openings that fit. Msg and data rates may
          apply. Reply STOP to opt out. See our{" "}
          <a
            href="https://www.hirealigned.com/privacypolicy"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Privacy Policy
          </a>{" "}
          and{" "}
          <a
            href="https://www.hirealigned.com/termsofservice"
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Terms of Service
          </a>
          .
        </span>
      </label>
      {error && (
        <p role="alert" className="text-sm text-red-300">
          {error}
        </p>
      )}
      <button
        type="submit"
        disabled={!ready || busy}
        className="min-h-12 w-full rounded-lg bg-white px-6 text-base font-semibold text-[#0A0F1F] disabled:opacity-40 sm:w-auto"
      >
        {busy ? "Starting..." : "Start my career check"}
      </button>
      <p className="text-sm">
        <Link href="/careers/move-up" className="underline text-white/70">
          Read: moving up in the trades
        </Link>
      </p>
    </form>
  )
}
